/**
 * Currency display metadata and ISO 4217 minor units.
 *
 * The minor-unit tables are the full ones, not just the currencies we happen to
 * show today — display precision is a property of the currency, so the rule has
 * to be complete or it will quietly be wrong the first time a new currency is
 * added.
 */

/**
 * Zero-minor-unit currencies exactly as ISO 4217 lists them (verified against
 * the SIX list published 2026-01-01). Sixteen codes, no more.
 */
const ISO_ZERO_DECIMAL = new Set([
  "BIF", "CLP", "DJF", "GNF", "ISK", "JPY", "KMF", "KRW",
  "PYG", "RWF", "UGX", "VND", "VUV", "XAF", "XOF", "XPF",
]);

/**
 * DELIBERATE DEPARTURES FROM ISO — each one a decision, not an oversight.
 *
 * ISO assigns these two minor units; everyday practice in their own countries
 * uses none, and this app shows people what they expect to read rather than
 * what a standard records. Rendering "1,250.00 HUF" is ISO-compliant and wrong
 * to the Hungarian reading it.
 *
 *   HUF  the fillér was withdrawn in 1999; prices are quoted in whole forint
 *   TWD  the cent is not in circulation; prices are quoted in whole dollars
 *   MGA  nominally five iraimbilanja, which have no practical circulation
 *
 * The validation gate asserts our table matches ISO EXCEPT for these three,
 * reading the set from here so the exception is enforced rather than remembered.
 */
export const CONVENTION_ZERO_DECIMAL = new Set(["HUF", "TWD", "MGA"]);

/** Currencies with three minor digits — mostly Gulf and North African dinars. */
const THREE_DECIMAL = new Set(["KWD", "BHD", "OMR", "JOD", "TND", "LYD", "IQD"]);

/**
 * Four minor digits. The metals are priced far finer than a national currency;
 * CLF and UYW are ISO four-decimal index units, handled here rather than
 * silently falling through to two.
 */
const FOUR_DECIMAL = new Set(["XAU", "XAG", "XPT", "XPD", "CLF", "UYW"]);

export function minorUnits(code: string): number {
  if (ISO_ZERO_DECIMAL.has(code) || CONVENTION_ZERO_DECIMAL.has(code)) return 0;
  if (THREE_DECIMAL.has(code)) return 3;
  if (FOUR_DECIMAL.has(code)) return 4;
  return 2;
}

/** The "1 X = Y" line always shows 4 decimals, per the product brief. */
export const UNIT_RATE_DECIMALS = 4;

/**
 * Extra precision for the unit-rate line when 4 decimals would round to zero —
 * a very low-value currency against a high-value one, e.g. 1 KWD in IDR.
 */
export const UNIT_RATE_DECIMALS_FINE = 6;

export interface CurrencyMeta {
  readonly code: string;
  readonly nameEn: string;
  /**
   * Used when a screen reader speaks a quantity: "0.2667 US Dollars" rather
   * than "0.2667 US Dollar". Stored rather than derived because English
   * currency plurals are irregular — Yen takes no "s".
   */
  readonly namePluralEn: string;
  /** Definite form, used as a standalone label. */
  readonly nameAr: string;
  /**
   * INDEFINITE form, used after a numeral.
   *
   * Arabic takes no definite article after a count, so reusing nameAr in the
   * unit-rate sentence produces "1 الريال السعودي", which is wrong. It has to
   * read "1 ريال سعودي". Stored rather than derived because stripping "ال"
   * mechanically would leave "ريال السعودي" — the article sits on both words.
   */
  readonly nameArIndefinite: string;
  /**
   * ISO 3166-1 alpha-2 used to pick the SVG in /public/flags.
   *
   * OPTIONAL, because the withdrawn codes below arrived before their flags did
   * and a placeholder is honest where an invented file would not be. Both the
   * row and the picker render .row__flag--none when it is absent.
   */
  readonly flag?: string;
  /**
   * Where it is spent, in both languages — SEARCHABLE, not decorative.
   *
   * People look for money by country far more often than by currency name:
   * "مصر" is a more natural search than "الجنيه المصري", and "japan" more
   * natural than "yen" for anyone who does not already know the answer. Without
   * these fields the picker can only be searched by people who already know
   * what they are looking for, which is the opposite of what a picker is for.
   */
  readonly countryEn?: string;
  readonly countryAr?: string;
  /**
   * What people actually type — colloquial, shortened, misspelled and English.
   *
   * The formal name is what a currency is called; an alias is what it is called
   * out loud. Someone in Cairo types "بكس" for dollars and "جنيه" expecting
   * their own pound, not Sterling. A picker that only matches formal names
   * serves people who already know the answer.
   *
   * Both scripts go in one list, deliberately: an Arabic UI does not mean an
   * Arabic keyboard, and half the Gulf types currency codes and English names on
   * a Latin layout out of habit.
   */
  readonly aliases?: readonly string[];
  /**
   * Tie-break weight, LOWER RANKS HIGHER. Only consulted between entries that
   * matched a query equally well.
   *
   * WHY THIS EXISTS. "ريال" matches Saudi, Qatari, Omani, Yemeni and Iranian;
   * "دينار" matches seven currencies; "جنيه" matches Egyptian, Sterling,
   * Sudanese and Syrian. Every one of those is an exact alias hit, so the tiers
   * in score() cannot separate them and the order falls to whatever the table
   * happens to list first — alphabetical accident deciding what a Gulf user
   * sees at the top of a search for their own money.
   *
   * So the order is AUTHORED. The number is a judgement about who is using this
   * app and what they convert, not a fact about the currency, which is exactly
   * why it lives in the data where it can be argued with rather than buried in
   * the ranking function.
   */
  readonly prominence?: number;
  /**
   * Withdrawn from circulation. Searchable, never selectable — someone who
   * knows the old code must find it and be told what replaced it, rather than
   * concluding the app does not have their currency.
   */
  readonly withdrawn?: { readonly replacedBy: string };
  /**
   * Pegged currencies are exact by policy rather than by market. Surfaced in the
   * currency info panel in a later step; recorded here so the fact is not lost.
   */
  readonly pegged?: { readonly to: string; readonly rate: string };
}

export const CURRENCY_META: Readonly<Record<string, CurrencyMeta>> = {
  /*
   * THE RUBLE IS A TWO-SOURCE CURRENCY, AND THAT IS PERMANENT.
   *
   * Verified against all three providers rather than assumed: Frankfurter, our
   * primary, does NOT carry RUB at all — it aggregates ECB reference rates and
   * the ECB suspended ruble publication in March 2022. The two fallbacks both
   * quote it, 0.23% apart, so the coverage engine reads two agreeing sources
   * and returns "ok" with no caveat, which is the correct verdict.
   *
   * Recorded here because the reason is structural, not transient: nobody
   * should later "fix" the missing primary source, and if one fallback drops it
   * the currency falls to single-source and becomes caveated on its own.
   *
   * No flag file yet — see the flag audit; ru.svg is not in public/flags.
   */
  RUB: {
    code: "RUB",
    nameEn: "Russian Ruble",
    namePluralEn: "Russian Rubles",
    nameAr: "الروبل الروسي",
    nameArIndefinite: "روبل روسي",
    countryEn: "Russia",
    countryAr: "روسيا",
    aliases: ["روبل", "الروبل", "روبل روسي", "روسي", "روسيا", "موسكو",
              "ruble", "rouble", "russian", "russia", "moscow", "rub"],
    /*
     * 40: below the Gulf and the big remittance corridors, above Nepal. "روبل"
     * does not collide with "روبية" once normalised — different words — so this
     * only orders the shared "رو" prefix, where the corridors come first.
     */
    prominence: 40,
  },
  IDR: {
    code: "IDR",
    nameEn: "Indonesian Rupiah",
    namePluralEn: "Indonesian Rupiah",
    nameAr: "الروبية الإندونيسية",
    nameArIndefinite: "روبية إندونيسية",
    countryEn: "Indonesia",
    countryAr: "إندونيسيا",
    aliases: ["روبية", "روبيه", "روبية اندونيسية", "اندونيسي", "اندونيسيا", "rupiah", "rupee", "indonesian", "indonesia", "idr", "rp"],
    prominence: 34,
  },
  NPR: {
    code: "NPR",
    nameEn: "Nepalese Rupee",
    namePluralEn: "Nepalese Rupees",
    nameAr: "الروبية النيبالية",
    nameArIndefinite: "روبية نيبالية",
    countryEn: "Nepal",
    countryAr: "نيبال",
    aliases: ["روبية", "روبيه", "نيبالي", "نيبال", "rupee", "rupees", "nepalese", "nepali", "nepal", "npr"],
    prominence: 43,
  },
  LKR: {
    code: "LKR",
    nameEn: "Sri Lankan Rupee",
    namePluralEn: "Sri Lankan Rupees",
    nameAr: "الروبية السريلانكية",
    nameArIndefinite: "روبية سريلانكية",
    countryEn: "Sri Lanka",
    countryAr: "سريلانكا",
    aliases: ["روبية", "روبيه", "سريلانكي", "سريلانكا", "سيلان", "rupee", "rupees", "sri lankan", "sri lanka", "ceylon", "lkr"],
    prominence: 33,
  },
  PHP: {
    code: "PHP",
    nameEn: "Philippine Peso",
    namePluralEn: "Philippine Pesos",
    nameAr: "البيزو الفلبيني",
    nameArIndefinite: "بيزو فلبيني",
    countryEn: "Philippines",
    countryAr: "الفلبين",
    aliases: ["بيزو", "بيسو", "فلبيني", "الفلبين", "peso", "pesos", "piso", "philippine", "philippines", "php"],
    prominence: 25,
  },
  BDT: {
    code: "BDT",
    nameEn: "Bangladeshi Taka",
    namePluralEn: "Bangladeshi Taka",
    nameAr: "التاكا البنغلاديشية",
    nameArIndefinite: "تاكا بنغلاديشية",
    countryEn: "Bangladesh",
    countryAr: "بنغلاديش",
    aliases: ["تاكا", "تاكه", "بنغلاديشي", "بنغلاديش", "بنجلاديش", "taka", "tk", "bangladeshi", "bangladesh", "bdt"],
    prominence: 24,
  },
  PKR: {
    code: "PKR",
    nameEn: "Pakistani Rupee",
    namePluralEn: "Pakistani Rupees",
    nameAr: "الروبية الباكستانية",
    nameArIndefinite: "روبية باكستانية",
    countryEn: "Pakistan",
    countryAr: "باكستان",
    aliases: ["روبية", "روبيه", "روبية باكستانية", "باكستاني", "باكستان", "rupee", "rupees", "pakistani", "pakistan", "pkr"],
    prominence: 23,
  },
  JOD: {
    code: "JOD",
    nameEn: "Jordanian Dinar",
    namePluralEn: "Jordanian Dinars",
    nameAr: "الدينار الأردني",
    nameArIndefinite: "دينار أردني",
    countryEn: "Jordan",
    countryAr: "الأردن",
    aliases: ["دينار", "دينار اردني", "اردني", "الاردن", "عمان الاردن", "dinar", "jordanian", "jordan", "amman", "jod"],
    prominence: 31,
    pegged: { to: "USD", rate: "0.709" },
  },
  OMR: {
    code: "OMR",
    nameEn: "Omani Rial",
    namePluralEn: "Omani Rials",
    nameAr: "الريال العماني",
    nameArIndefinite: "ريال عماني",
    countryEn: "Oman",
    countryAr: "عُمان",
    aliases: ["ريال", "ريال عماني", "عماني", "عمان", "مسقط", "rial", "riyal", "omani", "oman", "muscat", "omr"],
    prominence: 30,
    pegged: { to: "USD", rate: "0.3845" },
  },
  BHD: {
    code: "BHD",
    nameEn: "Bahraini Dinar",
    namePluralEn: "Bahraini Dinars",
    nameAr: "الدينار البحريني",
    nameArIndefinite: "دينار بحريني",
    countryEn: "Bahrain",
    countryAr: "البحرين",
    aliases: ["دينار", "دينار بحريني", "بحريني", "البحرين", "المنامة", "dinar", "bahraini", "bahrain", "manama", "bhd", "bd"],
    prominence: 21,
    pegged: { to: "USD", rate: "0.376" },
  },
  QAR: {
    code: "QAR",
    nameEn: "Qatari Riyal",
    namePluralEn: "Qatari Riyals",
    nameAr: "الريال القطري",
    nameArIndefinite: "ريال قطري",
    countryEn: "Qatar",
    countryAr: "قطر",
    aliases: ["ريال", "ريال قطري", "قطري", "قطر", "الدوحة", "riyal", "rial", "qatari", "qatar", "doha", "qar", "qr"],
    prominence: 20,
    pegged: { to: "USD", rate: "3.64" },
  },
  /*
   * WITHDRAWN — searchable, never selectable, and each names its replacement.
   *
   * Someone who knows the old code must be able to find it and be TOLD what
   * happened, rather than concluding the app is missing their money. That is
   * the whole reason these two are in the table at all: they are the only
   * entries here that exist to be refused.
   *
   * No flag yet. A placeholder is honest; a wrong flag file is not.
   */
  MRO: {
    code: "MRO",
    nameEn: "Mauritanian Ouguiya (old)",
    namePluralEn: "Mauritanian Ouguiya (old)",
    nameAr: "الأوقية الموريتانية (القديمة)",
    nameArIndefinite: "أوقية موريتانية قديمة",
    countryEn: "Mauritania",
    countryAr: "موريتانيا",
    withdrawn: { replacedBy: "MRU" },
  },
  MRU: {
    code: "MRU",
    nameEn: "Mauritanian Ouguiya",
    namePluralEn: "Mauritanian Ouguiya",
    nameAr: "الأوقية الموريتانية",
    nameArIndefinite: "أوقية موريتانية",
    countryEn: "Mauritania",
    countryAr: "موريتانيا",
  },
  ANG: {
    code: "ANG",
    nameEn: "Netherlands Antillean Guilder",
    namePluralEn: "Netherlands Antillean Guilders",
    nameAr: "غيلدر الأنتيل الهولندية",
    nameArIndefinite: "غيلدر أنتيلي",
    countryEn: "Curacao and Sint Maarten",
    countryAr: "كوراساو وسينت مارتن",
    withdrawn: { replacedBy: "XCG" },
  },
  XCG: {
    code: "XCG",
    nameEn: "Caribbean Guilder",
    namePluralEn: "Caribbean Guilders",
    nameAr: "الغيلدر الكاريبي",
    nameArIndefinite: "غيلدر كاريبي",
    countryEn: "Curacao and Sint Maarten",
    countryAr: "كوراساو وسينت مارتن",
  },
  USD: {
    code: "USD",
    nameEn: "US Dollar",
    namePluralEn: "US Dollars",
    nameAr: "الدولار الأمريكي",
    nameArIndefinite: "دولار أمريكي",
    flag: "us",
    countryEn: "United States",
    countryAr: "الولايات المتحدة",
    aliases: ["دولار", "دولار امريكي", "امريكي", "امريكا", "بكس", "باكس", "خضراء", "dollar", "buck", "bucks", "american", "america", "usd", "usa"],
    prominence: 1,
  },
  EUR: {
    code: "EUR",
    nameEn: "Euro",
    namePluralEn: "Euros",
    nameAr: "اليورو",
    nameArIndefinite: "يورو",
    flag: "eu",
    countryEn: "Eurozone",
    countryAr: "منطقة اليورو",
    aliases: ["يورو", "اليورو", "أورو", "euro", "eur", "europe", "european"],
    prominence: 2,
  },
  GBP: {
    code: "GBP",
    nameEn: "Pound Sterling",
    namePluralEn: "Pounds Sterling",
    nameAr: "الجنيه الإسترليني",
    nameArIndefinite: "جنيه إسترليني",
    flag: "gb",
    countryEn: "United Kingdom",
    countryAr: "المملكة المتحدة",
    aliases: ["جنيه", "استرليني", "جنيه استرليني", "اسطرليني", "بريطاني", "بريطانيا", "لندن", "pound", "sterling", "quid", "british", "britain", "uk", "gbp"],
    prominence: 22,
  },
  JPY: {
    code: "JPY",
    nameEn: "Japanese Yen",
    // Yen has no plural form in English.
    namePluralEn: "Japanese Yen",
    nameAr: "الين الياباني",
    nameArIndefinite: "ين ياباني",
    flag: "jp",
    countryEn: "Japan",
    countryAr: "اليابان",
  },
  SAR: {
    code: "SAR",
    nameEn: "Saudi Riyal",
    namePluralEn: "Saudi Riyals",
    nameAr: "الريال السعودي",
    nameArIndefinite: "ريال سعودي",
    flag: "sa",
    countryEn: "Saudi Arabia",
    countryAr: "السعودية",
    aliases: ["ريال", "ريال سعودي", "سعودي", "ريإل", "riyal", "rial", "saudi", "sar", "sr"],
    prominence: 10,
    pegged: { to: "USD", rate: "3.75" },
  },
  AED: {
    code: "AED",
    nameEn: "UAE Dirham",
    namePluralEn: "UAE Dirhams",
    nameAr: "الدرهم الإماراتي",
    nameArIndefinite: "درهم إماراتي",
    flag: "ae",
    countryEn: "United Arab Emirates",
    countryAr: "الإمارات",
    aliases: ["درهم", "درهم اماراتي", "اماراتي", "الامارات", "دبي", "ابوظبي", "dirham", "dhs", "emirates", "dubai", "abu dhabi", "uae", "aed"],
    prominence: 14,
    pegged: { to: "USD", rate: "3.6725" },
  },
  EGP: {
    code: "EGP",
    nameEn: "Egyptian Pound",
    namePluralEn: "Egyptian Pounds",
    nameAr: "الجنيه المصري",
    nameArIndefinite: "جنيه مصري",
    flag: "eg",
    countryEn: "Egypt",
    countryAr: "مصر",
    aliases: ["جنيه", "جنيه مصري", "مصري", "مصر", "الجنيه", "قرش", "pound", "egyptian", "egypt", "egp", "le"],
    prominence: 12,
  },
  CHF: {
    code: "CHF",
    nameEn: "Swiss Franc",
    namePluralEn: "Swiss Francs",
    nameAr: "الفرنك السويسري",
    nameArIndefinite: "فرنك سويسري",
    flag: "ch",
    countryEn: "Switzerland",
    countryAr: "سويسرا",
  },
  CAD: {
    code: "CAD",
    nameEn: "Canadian Dollar",
    namePluralEn: "Canadian Dollars",
    nameAr: "الدولار الكندي",
    nameArIndefinite: "دولار كندي",
    flag: "ca",
    countryEn: "Canada",
    countryAr: "كندا",
  },
  AUD: {
    code: "AUD",
    nameEn: "Australian Dollar",
    namePluralEn: "Australian Dollars",
    nameAr: "الدولار الأسترالي",
    nameArIndefinite: "دولار أسترالي",
    flag: "au",
    countryEn: "Australia",
    countryAr: "أستراليا",
  },
  INR: {
    code: "INR",
    nameEn: "Indian Rupee",
    namePluralEn: "Indian Rupees",
    nameAr: "الروبية الهندية",
    nameArIndefinite: "روبية هندية",
    flag: "in",
    countryEn: "India",
    countryAr: "الهند",
    aliases: ["روبية", "روبيه", "روبية هندية", "هندي", "الهند", "rupee", "rupees", "indian", "india", "inr", "rs"],
    prominence: 13,
  },
  KWD: {
    code: "KWD",
    nameEn: "Kuwaiti Dinar",
    namePluralEn: "Kuwaiti Dinars",
    nameAr: "الدينار الكويتي",
    nameArIndefinite: "دينار كويتي",
    flag: "kw",
    countryEn: "Kuwait",
    countryAr: "الكويت",
    aliases: ["دينار", "دينار كويتي", "كويتي", "الكويت", "kd", "dinar", "kuwaiti", "kuwait", "kwd"],
    prominence: 11,
  },
  SDG: {
    code: "SDG",
    nameEn: "Sudanese Pound",
    namePluralEn: "Sudanese Pounds",
    nameAr: "الجنيه السوداني",
    nameArIndefinite: "جنيه سوداني",
    flag: "sd",
    countryEn: "Sudan",
    countryAr: "السودان",
  },
  SYP: {
    code: "SYP",
    nameEn: "Syrian Pound",
    namePluralEn: "Syrian Pounds",
    nameAr: "الليرة السورية",
    nameArIndefinite: "ليرة سورية",
    flag: "sy",
    countryEn: "Syria",
    countryAr: "سوريا",
  },
};

export function currencyMeta(code: string): CurrencyMeta {
  return (
    CURRENCY_META[code] ?? {
      code,
      nameEn: code,
      namePluralEn: code,
      nameAr: code,
      nameArIndefinite: code,
      // No flag file: the component renders a neutral placeholder rather than
      // a broken image.
      flag: "",
    }
  );
}

export function currencyName(code: string, locale: "en" | "ar"): string {
  const meta = currencyMeta(code);
  return locale === "ar" ? meta.nameAr : meta.nameEn;
}

/**
 * The form to use AFTER A QUANTITY.
 * English pluralises; Arabic drops the definite article instead.
 */
export function currencyNameCounted(code: string, locale: "en" | "ar"): string {
  const meta = currencyMeta(code);
  return locale === "ar" ? meta.nameArIndefinite : meta.namePluralEn;
}
